'use client';

// Reportes gerenciales (MÓDULO 7). Panel con pestañas: Resumen (KPIs globales),
// Instrucción / Juzgamiento / Segunda instancia (tarjetas de conteos por instancia)
// y Reporte de expedientes (tabla paginada con filtros + exportación CSV/Excel).
// RBAC por rol de localStorage ('usuario'): solo ADMINISTRADOR y los directores.
// Los errores del backend llegan como { message } y se muestran como aviso centrado.

import { useCallback, useEffect, useState } from 'react';
import {
  BarChart3, Download, FileSpreadsheet, FileText, Gavel, Pencil,
  RefreshCcw, Scale,
} from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import HeroVista from '@/components/HeroVista';
import {
  dashboardInstruccion, dashboardJuzgamiento, dashboardSegunda, kpis,
  listarExpedientes, exportarExpedientes, etiquetaEstado,
  type DashboardInstruccion, type DashboardJuzgamiento, type DashboardSegunda,
  type Kpis, type ExpedienteReporte, type FiltrosExpedientes,
} from '@/lib/reportes';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const TODOS = '__TODOS__';
const LIMIT = 20;

// Estados disponibles para el filtro del reporte de expedientes.
const ESTADOS = [
  'QUEJA_RECIBIDA', 'SIN_REPARTO', 'INDAGACION_PREVIA', 'INVESTIGACION_DISCIPLINARIA',
  'EN_PRUEBAS', 'PLIEGO_DE_CARGOS', 'ARCHIVO', 'REMITIDO', 'EN_JUZGAMIENTO',
  'EN_ALEGATOS', 'FALLO', 'SEGUNDA_INSTANCIA', 'EJECUTORIADO', 'EN_EJECUCION',
];

// Tarjeta de conteo con número grande + etiqueta.
function TarjetaConteo({
  valor, label, icon: Icon, resaltar,
}: {
  valor: number | string;
  label: string;
  icon?: typeof BarChart3;
  resaltar?: 'alerta' | 'ok';
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardDescription>{label}</CardDescription>
        {Icon && <Icon className="size-5 text-primary" />}
      </CardHeader>
      <CardContent>
        <div
          className={
            resaltar === 'alerta'
              ? 'text-3xl font-semibold text-destructive'
              : resaltar === 'ok'
                ? 'text-3xl font-semibold text-primary'
                : 'text-3xl font-semibold'
          }
        >
          {valor}
        </div>
      </CardContent>
    </Card>
  );
}

function fmtFecha(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('es-CO');
}

export default function Reportes() {
  return (
    <div className="space-y-6">
      <HeroVista
        icono={BarChart3}
        titulo="Reportes gerenciales"
        descripcion="Indicadores del proceso disciplinario por instancia y reporte de expedientes exportable."
      />

      <Tabs defaultValue="resumen">
        <TabsList className="flex-wrap">
          <TabsTrigger value="resumen">Resumen</TabsTrigger>
          <TabsTrigger value="instruccion">Instrucción</TabsTrigger>
          <TabsTrigger value="juzgamiento">Juzgamiento</TabsTrigger>
          <TabsTrigger value="segunda">Segunda instancia</TabsTrigger>
          <TabsTrigger value="expedientes">Reporte de expedientes</TabsTrigger>
        </TabsList>

        <TabsContent value="resumen"><PanelResumen /></TabsContent>
        <TabsContent value="instruccion"><PanelInstruccion /></TabsContent>
        <TabsContent value="juzgamiento"><PanelJuzgamiento /></TabsContent>
        <TabsContent value="segunda"><PanelSegunda /></TabsContent>
        <TabsContent value="expedientes"><PanelExpedientes /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---- Resumen: KPIs globales -----------------------------------------------

function PanelResumen() {
  const [data, setData] = useState<Kpis | null>(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setData(await kpis());
    } catch (e) {
      avisoError('No se pudieron cargar los indicadores', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>;
  }
  if (!data) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Sin datos.</p>;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <TarjetaConteo valor={data.totalExpedientes} label="Total de expedientes" icon={FileText} />
      <TarjetaConteo valor={`${data.porcentajeArchivos}%`} label="Porcentaje de archivos" icon={BarChart3} resaltar="ok" />
      <TarjetaConteo valor={`${data.porcentajeConPliego}%`} label="Porcentaje con pliego" icon={FileText} />
      <TarjetaConteo valor={data.proximosAPrescribir} label="Próximos a prescribir" icon={Scale} resaltar="alerta" />
      <TarjetaConteo valor={data.terminosVencidos} label="Términos vencidos" icon={Scale} resaltar="alerta" />
    </div>
  );
}

// ---- Instrucción ----------------------------------------------------------

function PanelInstruccion() {
  const [data, setData] = useState<DashboardInstruccion | null>(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setData(await dashboardInstruccion());
    } catch (e) {
      avisoError('No se pudo cargar el panel de instrucción', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>;
  }
  if (!data) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Sin datos.</p>;
  }

  const porEstado = Object.entries(data.porEstado ?? {});

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <TarjetaConteo valor={data.quejasTotales} label="Notificaciones disciplinarias totales" icon={FileText} />
        <TarjetaConteo valor={data.sinReparto} label="Sin reparto" icon={Pencil} resaltar="alerta" />
        <TarjetaConteo valor={data.indagacionesActivas} label="Indagaciones activas" />
        <TarjetaConteo valor={data.investigacionesActivas} label="Investigaciones activas" />
        <TarjetaConteo valor={data.enPruebas} label="En pruebas" />
        <TarjetaConteo valor={data.proximosAPrescribir} label="Próximos a prescribir" resaltar="alerta" />
        <TarjetaConteo valor={data.vencidos} label="Vencidos" resaltar="alerta" />
        <TarjetaConteo valor={data.pliegos} label="Pliegos" />
        <TarjetaConteo valor={data.archivos} label="Archivos" resaltar="ok" />
        <TarjetaConteo valor={data.remitidos} label="Remitidos" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Distribución por estado</CardTitle>
        </CardHeader>
        <CardContent>
          {porEstado.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin distribución por estado.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {porEstado.map(([estado, conteo]) => (
                <Badge key={estado} variant="secondary" className="text-sm">
                  {etiquetaEstado(estado)}: <span className="ml-1 font-semibold">{conteo}</span>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---- Juzgamiento ----------------------------------------------------------

function PanelJuzgamiento() {
  const [data, setData] = useState<DashboardJuzgamiento | null>(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setData(await dashboardJuzgamiento());
    } catch (e) {
      avisoError('No se pudo cargar el panel de juzgamiento', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>;
  }
  if (!data) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Sin datos.</p>;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <TarjetaConteo valor={data.recibidos} label="Recibidos" icon={Gavel} />
      <TarjetaConteo valor={data.ordinarios} label="Procesos ordinarios" />
      <TarjetaConteo valor={data.verbales} label="Procesos verbales" />
      <TarjetaConteo valor={data.enAlegatos} label="En alegatos" />
      <TarjetaConteo valor={data.variacion} label="En variación" />
      <TarjetaConteo valor={data.fallos} label="Fallos" />
      <TarjetaConteo valor={data.recursos} label="Recursos" />
      <TarjetaConteo valor={data.aSegundaInstancia} label="A segunda instancia" />
    </div>
  );
}

// ---- Segunda instancia ----------------------------------------------------

function PanelSegunda() {
  const [data, setData] = useState<DashboardSegunda | null>(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setData(await dashboardSegunda());
    } catch (e) {
      avisoError('No se pudo cargar el panel de segunda instancia', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>;
  }
  if (!data) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Sin datos.</p>;
  }

  const porSentido = Object.entries(data.falloSegundaPorSentido ?? {});

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <TarjetaConteo valor={data.enSegundaInstancia} label="En segunda instancia" icon={Scale} />
        <TarjetaConteo valor={data.ejecutoriados} label="Ejecutoriados" />
        <TarjetaConteo valor={data.enEjecucion} label="En ejecución" />
        <TarjetaConteo valor={data.cumplidas} label="Sanciones cumplidas" resaltar="ok" />
        <TarjetaConteo valor={data.archivoHistorico} label="Archivo histórico" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fallos de segunda instancia por sentido</CardTitle>
        </CardHeader>
        <CardContent>
          {porSentido.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin fallos registrados.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {porSentido.map(([sentido, conteo]) => (
                <Badge key={sentido} variant="secondary" className="text-sm">
                  {etiquetaEstado(sentido)}: <span className="ml-1 font-semibold">{conteo}</span>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---- Reporte de expedientes -----------------------------------------------

function PanelExpedientes() {
  const [items, setItems] = useState<ExpedienteReporte[]>([]);
  const [meta, setMeta] = useState({ page: 1, limit: LIMIT, total: 0, totalPages: 1 });
  const [cargando, setCargando] = useState(true);
  const [exportando, setExportando] = useState<'csv' | 'excel' | null>(null);
  const [estado, setEstado] = useState<string>(TODOS);
  const [anio, setAnio] = useState<string>('');
  const [funcionarioId, setFuncionarioId] = useState<string>('');
  const [page, setPage] = useState(1);

  // Filtros efectivos que se envían al backend (sin la paginación).
  const filtros: FiltrosExpedientes = {
    ...(estado !== TODOS ? { estado } : {}),
    ...(anio ? { anio } : {}),
    ...(funcionarioId ? { funcionarioId } : {}),
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await listarExpedientes({ ...filtros, page, limit: LIMIT });
      setItems(res.data ?? []);
      if (res.meta) setMeta(res.meta);
    } catch (e) {
      avisoError('No se pudo cargar el reporte de expedientes', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, anio, funcionarioId, page]);

  useEffect(() => { cargar(); }, [cargar]);

  async function exportar(formato: 'csv' | 'excel') {
    setExportando(formato);
    try {
      await exportarExpedientes(formato, filtros);
      avisoExito(`Reporte exportado (${formato === 'excel' ? 'Excel' : 'CSV'}).`);
    } catch (e) {
      avisoError('No se pudo exportar el reporte', e instanceof Error ? e.message : undefined);
    } finally {
      setExportando(null);
    }
  }

  const totalPages = meta.totalPages || 1;

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label>Estado</Label>
            <Select
              value={estado}
              onValueChange={(v) => { setEstado(v); setPage(1); }}
            >
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos</SelectItem>
                {ESTADOS.map((e) => (
                  <SelectItem key={e} value={e}>{etiquetaEstado(e)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Año</Label>
            <Input
              type="number"
              placeholder="Ej. 2026"
              className="w-32"
              value={anio}
              onChange={(ev) => { setAnio(ev.target.value); setPage(1); }}
            />
          </div>
          <div className="space-y-2">
            <Label>Funcionario (ID)</Label>
            <Input
              placeholder="Opcional"
              className="w-52"
              value={funcionarioId}
              onChange={(ev) => { setFuncionarioId(ev.target.value); setPage(1); }}
            />
          </div>
          <Button variant="outline" onClick={() => cargar()} disabled={cargando}>
            <RefreshCcw className="size-4" /> Actualizar
          </Button>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              onClick={() => exportar('csv')}
              disabled={exportando !== null}
            >
              <Download className="size-4" /> Exportar CSV
            </Button>
            <Button
              onClick={() => exportar('excel')}
              disabled={exportando !== null}
            >
              <FileSpreadsheet className="size-4" /> Exportar Excel
            </Button>
          </div>
        </div>

        {cargando ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Radicado</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Etapa</TableHead>
                  <TableHead>Fecha notificación</TableHead>
                  <TableHead>Prescripción</TableHead>
                  <TableHead>Funcionario</TableHead>
                  <TableHead>Prioridad</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((x) => (
                  <TableRow key={x.radicado}>
                    <TableCell className="font-medium">{x.radicado}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{etiquetaEstado(x.estado_actual)}</Badge>
                    </TableCell>
                    <TableCell>{x.etapa_actual ? etiquetaEstado(x.etapa_actual) : '—'}</TableCell>
                    <TableCell>{fmtFecha(x.fecha_queja)}</TableCell>
                    <TableCell>{fmtFecha(x.fecha_prescripcion)}</TableCell>
                    <TableCell>{x.funcionario_actual || '—'}</TableCell>
                    <TableCell>{x.prioridad || '—'}</TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      No hay expedientes para los filtros seleccionados.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">
                Página {meta.page} de {totalPages} · {meta.total} en total
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  disabled={meta.page <= 1 || cargando}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline" size="sm"
                  disabled={meta.page >= totalPages || cargando}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
